import React, { useState } from 'react';
import {
  RectangleStackIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import {
  useFlyerTemplates,
  useToggleFlyerTemplate,
  useToggleFeatureFlyerTemplate,
  useDeleteFlyerTemplate,
} from '../hooks/useFlyerTemplates';
import { FlyerTemplate } from '../services/api';
import FlyerTemplateFormModal from '../components/flyer-templates/FlyerTemplateFormModal';

const resolveUrl = (url: string) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const bpeBeUrl = process.env.REACT_APP_BPE_BE_URL || 'http://localhost:5000';
  return `${bpeBeUrl}${url}`;
};

const FlyerTemplates: React.FC = () => {
  const { data: templates, isLoading } = useFlyerTemplates();
  const toggleActive = useToggleFlyerTemplate();
  const toggleFeature = useToggleFeatureFlyerTemplate();
  const deleteTemplate = useDeleteFlyerTemplate();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selected, setSelected] = useState<FlyerTemplate | null>(null);
  const [toDelete, setToDelete] = useState<FlyerTemplate | null>(null);

  const list = templates || [];

  const handleCreate = () => {
    setSelected(null);
    setIsFormOpen(true);
  };

  const handleEdit = (t: FlyerTemplate) => {
    setSelected(t);
    setIsFormOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Flyer Templates
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage dealer marketing flyer templates and QR placement
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          New Template
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Total Templates
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {list.length}
              </p>
            </div>
            <RectangleStackIcon className="h-10 w-10 text-gray-400" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {list.filter((t) => t.isActive).length}
              </p>
            </div>
            <EyeIcon className="h-10 w-10 text-green-400" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Featured
              </p>
              <p className="text-2xl font-bold text-amber-500">
                {list.filter((t) => t.isFeatured).length}
              </p>
            </div>
            <StarSolidIcon className="h-10 w-10 text-amber-400" />
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
        {isLoading ? (
          <div className="p-8">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-24 bg-gray-200 dark:bg-gray-700 rounded"
                />
              ))}
            </div>
          </div>
        ) : list.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  {['Preview', 'Title', 'Category', 'Lang', 'Countries', 'Status', 'Actions'].map(
                    (h) => (
                      <th
                        key={h}
                        className={`px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${
                          h === 'Actions' ? 'text-right' : 'text-left'
                        }`}
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {list.map((t) => (
                  <tr
                    key={t._id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-900/40"
                  >
                    <td className="px-6 py-4">
                      <div className="relative h-16 w-12 rounded overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900">
                        <img
                          src={resolveUrl(t.imageUrl)}
                          alt={t.title}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.opacity = '0';
                          }}
                        />
                        {t.isFeatured && (
                          <StarSolidIcon className="absolute top-0.5 right-0.5 h-4 w-4 text-amber-400 drop-shadow" />
                        )}
                        {(t.pages?.length ?? 0) > 1 && (
                          <span className="absolute bottom-0.5 right-0.5 px-1 rounded bg-gray-900/70 text-[9px] font-semibold text-white">
                            {t.pages.length}p
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {t.title}
                      </p>
                      <div className="flex items-center gap-2">
                        {t.sourceType === 'pdf' && (
                          <span className="text-[10px] font-semibold text-purple-600 dark:text-purple-300">
                            PDF
                          </span>
                        )}
                        {t.hasQr === false && (
                          <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-300">
                            No QR
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                        {t.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 uppercase">
                      {t.language}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                      {t.countries.length
                        ? t.countries.slice(0, 3).join(', ') +
                          (t.countries.length > 3
                            ? ` +${t.countries.length - 3}`
                            : '')
                        : 'All'}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          t.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {t.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          title={t.isFeatured ? 'Unfeature' : 'Feature'}
                          onClick={() => toggleFeature.mutate(t._id)}
                          className="p-1.5 text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded"
                        >
                          {t.isFeatured ? (
                            <StarSolidIcon className="h-4 w-4" />
                          ) : (
                            <StarIcon className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          title={t.isActive ? 'Deactivate' : 'Activate'}
                          onClick={() => toggleActive.mutate(t._id)}
                          className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                        >
                          {t.isActive ? (
                            <EyeSlashIcon className="h-4 w-4" />
                          ) : (
                            <EyeIcon className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          title="Edit"
                          onClick={() => handleEdit(t)}
                          className="p-1.5 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          title="Delete"
                          onClick={() => setToDelete(t)}
                          className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <RectangleStackIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No templates yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Upload your first flyer template to get started
            </p>
            <button
              onClick={handleCreate}
              className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              New Template
            </button>
          </div>
        )}
      </div>

      <FlyerTemplateFormModal
        template={selected}
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setSelected(null);
        }}
      />

      {/* Delete confirmation */}
      {toDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 text-center">
            <div
              className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75"
              onClick={() => setToDelete(null)}
            />
            <div className="inline-block w-full max-w-md px-4 pt-5 pb-4 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 rounded-lg shadow-xl sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 dark:bg-red-900 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                  <TrashIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Delete Template
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Are you sure you want to delete "{toDelete.title}"? This
                    action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  disabled={deleteTemplate.isPending}
                  onClick={() => {
                    deleteTemplate.mutate(toDelete._id);
                    setToDelete(null);
                  }}
                  className="inline-flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 sm:ml-3 sm:w-auto disabled:opacity-50"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setToDelete(null)}
                  className="inline-flex justify-center w-full px-4 py-2 mt-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlyerTemplates;
